import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import fr from 'facet-rest';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  advisorWhatIfPageUri,
  householdEditRecordExpenseUri,
  householdEditRecordIncomeDetailsUri,
  householdEditRecordInsuranceUri,
  householdEditRecordLiabilitiesUri,
  householdEditRecordPersonalInformationUri, householdEditRecordRealAssetsUri,
  householdEditRecordUri,
  householdNewPrintUri,
  householdSecureFilesUri,
  taskBoardUri,
} from 'Consts/advisorUris';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import {
  useSearchBarDispatch,
  useSearchBarState,
} from 'Components/AdvisorApp/Header/ClientSearchBar/BetaSearchBar/SearchBarContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import ButtonGroupWithMenu from 'Components/UI/ButtonGroupWithMenu';
import {
  BALANCE_SHEET,
  CASH_FLOW,
  GOALS_AND_OBV,
  HOLDINGS,
  HOLDINGS_COMPARISON,
} from 'Components/UI/Snapshot/Snapshot';
import { hasRelationship } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';
import { buildPageLink } from 'Views/Page/Content/Templates';
import { advisorPageMessages } from 'Consts/Strings';
import { missionControlURL } from 'Hooks/useMissionControlURL';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';
import { navigateAndClose } from './utils';

const styles = (theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  linkButton: {
    marginRight: theme.spacing(1),
  },
  button: {
    fontWeight: theme.typography.fontWeightFacetBold,
    fontSize: theme.typography.pxToRem(11),
  },
  buttonGroupRoot: {
    marginRight: theme.spacing(1),
  },
  buttonGroupDropDownIcon: {
    fontSize: '1.25rem',
  },
});

export const isHidden = (args) => {
  return (item) => {
    if (!item.hidden) {
      return true;
    }
    switch (typeof item.hidden) {
      case 'function':
        return !item.hidden(args);

      default:
        return !item.hidden;
    }
  };
};

const ITEMS = [
  {
    label: 'Mission Control',
    itemKey: 'mission-control',
    to: (searchResultResource) => missionControlURL(searchResultResource, searchResultResource.attr('householdId')),
  },
  {
    label: 'Messages',
    itemKey: 'messages',
    to: (searchResultResource) => {
      const link = searchResultResource?.withRelationship(fr.relationships.messagesPage.name, (rel) => {
        return buildPageLink(
          rel.link('related'),
          advisorPageMessages,
          { householdId: searchResultResource.attr('householdId') }, // params
        );
      }, () => {
        return '';
      });
      return link;
    },
  },
  {
    label: 'Balance Sheet',
    itemKey: 'balanceSheet',
    hidden: ({ searchResultResource }) => searchResultResource.withRelationship(fr.relationships.householdBalanceSheetPage.name, () => false, () => true),
    to: (searchResultResource) => {
      return balanceSheetPageURL(searchResultResource, searchResultResource.attr('householdId'), true);
    },
  },
  {
    label: 'Files',
    itemKey: 'files',
    to: (searchResultResource) => {
      return householdSecureFilesUri(searchResultResource.attr('householdId'));
    },
  },
  {
    label: 'Snapshot',
    itemKey: 'snapshot',
    to: (searchResultResource) => {
      return householdNewPrintUri(searchResultResource.attr('householdId'));
    },
    childLinks: [
      {
        label: 'Balance Sheet',
        itemKey: 'snapshot-balance-sheet',
        to: (searchResultResource) => {
          return householdNewPrintUri(searchResultResource.attr('householdId'), {
            tab: BALANCE_SHEET,
          });
        },
      },
      {
        label: 'Cash Flow',
        itemKey: 'snapshot-cash-flow',
        to: (searchResultResource) => {
          return householdNewPrintUri(searchResultResource.attr('householdId'), {
            tab: CASH_FLOW,
          });
        },
      },
      {
        label: 'Goals',
        itemKey: 'snapshot-goals',
        to: (searchResultResource) => {
          return householdNewPrintUri(searchResultResource.attr('householdId'), {
            tab: GOALS_AND_OBV,
          });
        },
      },
      {
        label: 'Holdings',
        itemKey: 'snapshot-holdings',
        to: (searchResultResource) => {
          return householdNewPrintUri(searchResultResource.attr('householdId'), {
            tab: HOLDINGS,
          });
        },
      },
      {
        label: 'Holdings Comparison',
        itemKey: 'snapshot-holdings-comparison',
        to: (searchResultResource) => {
          return householdNewPrintUri(searchResultResource.attr('householdId'), {
            tab: HOLDINGS_COMPARISON,
          });
        },
      },
    ],
  },
  {
    label: 'Tasks',
    itemKey: 'tasks',
    to: (searchResultResource) => {
      return taskBoardUri({
        link: searchResultResource.relationship(fr.relationships.householdActionItems.name).link('related').url(),
      });
    },
  },
  {
    label: 'Record',
    itemKey: 'record',
    to: (searchResultResource) => {
      return householdEditRecordUri(searchResultResource.attr('householdId'));
    },
    childLinks: [
      {
        label: 'Personal Information',
        itemKey: 'record-personal',
        to: (searchResultResource) => {
          return householdEditRecordPersonalInformationUri(searchResultResource.attr('householdId'));
        },
      },
      {
        label: 'Income',
        itemKey: 'record-income',
        to: (searchResultResource) => {
          return householdEditRecordIncomeDetailsUri(searchResultResource.attr('householdId'));
        },
      },
      {
        label: 'Assets',
        itemKey: 'record-assets',
        to: (searchResultResource) => {
          return householdEditRecordRealAssetsUri(searchResultResource.attr('householdId'));
        },
      },
      {
        label: 'Liabilities',
        itemKey: 'record-liabilities',
        to: (searchResultResource) => {
          return householdEditRecordLiabilitiesUri(searchResultResource.attr('householdId'));
        },
      },
      {
        label: 'Insurance',
        itemKey: 'record-insurance',
        to: (searchResultResource) => {
          return householdEditRecordInsuranceUri(searchResultResource.attr('householdId'));
        },
      },
      {
        label: 'Expenses',
        itemKey: 'record-expenses',
        to: (searchResultResource) => {
          return householdEditRecordExpenseUri(searchResultResource.attr('householdId'));
        },
      },
    ],
  },
  {
    label: 'Roadmaps',
    itemKey: 'scenarios',
    hidden: ({ searchResultResource }) => !hasRelationship(searchResultResource, fr.relationships.facetPlan.name),
    to: (searchResultResource) => {
      const planUrl = searchResultResource.relationship(fr.relationships.facetPlan.name)?.link('related').url();
      return advisorWhatIfPageUri(planUrl, searchResultResource.attr('householdId'));
    },
  },
];

const NavigationItems = ({
  className,
  classes,
  searchResultResource,
}) => {
  const { userResource } = useUserContext();
  const eventTracking = useEventTrackingLogEvent();
  const searchBarDispatch = useSearchBarDispatch();
  const searchBarState = useSearchBarState();
  const navigateOnClick = navigateAndClose(eventTracking, searchBarState, searchBarDispatch);

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      {ITEMS.filter(isHidden({ userResource, searchResultResource })).map((item) => {
        const {
          label,
          to,
          childLinks,
          itemKey,
        } = item;
        const navItemId = `navigation-item-${itemKey}`;
        if (!childLinks) {
          return (
            <Button
              key={itemKey}
              className={classNames(classes.linkButton, classes.button)}
              color={'primaryBlue'}
              disableElevation
              variant={'faded-contained'}
              padding={'dense'}
              size={'default'}
              component={Link}
              onClick={(evt) => {
                evt.stopPropagation();
                return navigateOnClick(to(searchResultResource), false);
              }}
              test={navItemId}
              to={to(searchResultResource)}
            >
              {label}
            </Button>
          );
        }

        return (
          <ButtonGroupWithMenu
            key={itemKey}
            className={classes.linkButton}
            classes={{
              root: classes.buttonGroupRoot,
              dropDownIcon: classes.buttonGroupDropDownIcon,
            }}
            variant={'contained'}
            text={label}
            ButtonGroupProps={{
              disableElevation: true,
              size: 'small',
            }}
            PrimaryButtonProps={{
              color: 'primaryBlue',
              className: classes.button,
              component: Link,
              padding: 'dense',
              disableElevation: true,
              size: 'default',
              test: navItemId,
              to: to(searchResultResource),
              onClick: (evt) => {
                evt.stopPropagation();
                return navigateOnClick(to(searchResultResource), false);
              },
              variant: 'faded-contained',
            }}
            SecondaryButtonProps={{
              color: 'primaryBlue',
              disableElevation: true,
              padding: 'none',
              size: 'default',
              test: `${navItemId}-dropdown-button`,
              variant: 'faded-contained',
            }}
            options={childLinks.map(({ itemKey: childItemKey, ...restChildLink }) => {
              const childNavItemId = `navigation-item-${childItemKey}`;

              return {
                ...restChildLink,
                test: childNavItemId,
                onClick: (evt) => {
                  evt.stopPropagation();
                  return navigateOnClick(restChildLink.to(searchResultResource), true);
                },
              };
            })}
          />
        );
      })}
    </Box>
  );
};

NavigationItems.defaultProps = {
  className: '',
};

NavigationItems.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  searchResultResource: PropTypes.object.isRequired,
};

export default withStyles(styles)(NavigationItems);
